import { WORLD_DOMAIN } from '../../../constants.js';
import type {
  CharacterAppearance,
  CharacterBasic,
  CharacterDraft,
  CharacterNature,
  LearnedTrait,
} from '../../../domain/types.js';
import { MAX_REASON, MAX_TRAITS } from './defaults.js';

const ALPHA_INITIAL = /^[A-Za-z]?$/;

export function validateBasic(basic: CharacterBasic): string | null {
  if (!basic.firstName.trim()) return 'First name is required.';
  if (basic.lastInitial !== undefined && basic.lastInitial !== '') {
    if (basic.lastInitial.length > 1) return 'Last initial is one character max.';
    if (!ALPHA_INITIAL.test(basic.lastInitial)) return 'Last initial must be a single letter.';
  }
  return null;
}

export function validateAppearance(appearance: CharacterAppearance | null): string | null {
  if (!appearance) return 'Complete the look section.';
  const required: Array<keyof CharacterAppearance> = [
    'skinColor', 'topStyle', 'topColor', 'bottomStyle', 'bottomColor', 'hairStyle', 'hairColor',
  ];
  for (const key of required) {
    if (!appearance[key]?.trim()) return 'Complete every look field.';
  }
  return null;
}

export function validateNature(nature: CharacterNature | null): string | null {
  if (!nature) return 'Set all nature sliders.';
  const keys = Object.keys(nature) as Array<keyof CharacterNature>;
  for (const key of keys) {
    const v = nature[key];
    if (!Number.isInteger(v) || v < WORLD_DOMAIN.natureSliderMin || v > WORLD_DOMAIN.natureSliderMax) {
      return 'Nature sliders must be whole numbers from 0 to 10.';
    }
  }
  return null;
}

export function validateDetailed(character: Pick<CharacterDraft, 'appearance' | 'nature'>): string | null {
  return validateAppearance(character.appearance) ?? validateNature(character.nature);
}

export function validateTraitDraft(trait: LearnedTrait): string | null {
  if (!trait.traitKey) return 'Pick a trait from the list.';
  if (!Number.isInteger(trait.strength) || trait.strength < 0 || trait.strength > 10) {
    return 'Trait strength must be 0–10.';
  }
  if (!trait.reason.trim()) return 'Every trait needs a reason (what happened).';
  if (trait.reason.length > MAX_REASON) return `Reasons cap at ${MAX_REASON} characters.`;
  return null;
}

export function validateTraits(traits: LearnedTrait[]): string | null {
  if (traits.length > MAX_TRAITS) return `Maximum ${MAX_TRAITS} learned traits.`;
  const seen = new Set<string>();
  for (const trait of traits) {
    const err = validateTraitDraft(trait);
    if (err) return err;
    if (seen.has(trait.traitKey)) return 'No duplicate traits — pick distinct ones.';
    seen.add(trait.traitKey);
  }
  return null;
}

export function isCharacterComplete(character: CharacterDraft): boolean {
  return (
    validateBasic(character.basic) === null
    && validateDetailed(character) === null
    && validateTraits(character.traits) === null
  );
}

export function canSaveAndContinue(character: CharacterDraft): boolean {
  return validateBasic(character.basic) === null && validateDetailed(character) === null;
}
